import { useMemo } from 'react';
import { createCompositeAdapter } from './compositeAdapter';
import { createRxdAdapter, _destroyRxdTransportForTests } from './rxdAdapter';
import {
  _destroyScormAgainBackendForTests,
  createScormAgainAdapter
} from './scormAgainAdapter';
import { _resetLearnerLanguagePreferenceForTests } from './learnerLanguagePreference';
import { LmsAdapter } from './types';

let cached: LmsAdapter | null = null;

/**
 * Keep session lifecycle reporting independent of per-slide quiz settings.
 * Each adapter detects its host lazily and gates only record*Interaction calls
 * using the current sequence settings.
 */
export function getLmsAdapter(): LmsAdapter {
  if (cached) return cached;

  cached = createCompositeAdapter([
    createRxdAdapter(),
    createScormAgainAdapter()
  ]);

  // One-line trail in the console makes it obvious which LMS host the SPA
  // actually wired up for this session — invaluable when triaging "why is
  // RXD silent" / "why does scorm-again time out" reports.
  console.info('[lms] active adapter:', cached.name);

  return cached;
}

/** Test-only escape hatch. Not exported through `index.ts` consumers. */
export function _resetLmsAdapterForTests(): void {
  cached = null;
  _destroyRxdTransportForTests();
  _destroyScormAgainBackendForTests();
  _resetLearnerLanguagePreferenceForTests();
}

export {
  fetchLearnerLanguagePreference,
  logLmsLanguage,
  peekLearnerLanguagePreference
} from './learnerLanguagePreference';

/**
 * Hook variant. The returned adapter is referentially stable across
 * renders, so it is safe to put into effect dependency arrays.
 */
export function useLmsAdapter(): LmsAdapter {
  return useMemo(() => getLmsAdapter(), []);
}

/** Returns whether an LMS host is currently detected. */
export function shouldSendInteractions(): boolean {
  return getLmsAdapter().isAvailable;
}

export type { LmsAdapter } from './types';
